import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Moon, Heart } from 'lucide-react';
import { cn } from '@/lib/utils';
import yimaAvatar from '@/assets/images/pages/yima.png';

/**
 * Yima Chatbot Widget
 * Premium AI assistant for YIM Burundi (Coming Soon state)
 * Gates Foundation-inspired design
 */
export function YimaChatbot() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const chatBodyRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <>
      {/* Floating Action Button (FAB) - Premium Design */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'fixed bottom-6 right-6 z-50',
          'w-16 h-16 rounded-full',
          'bg-white',
          'shadow-[0_8px_32px_rgba(30,64,175,0.25)]',
          'flex items-center justify-center',
          'transition-all duration-500',
          'hover:shadow-[0_12px_40px_rgba(30,64,175,0.35)]',
          'hover:scale-105',
          'focus:outline-none focus:ring-4 focus:ring-[#1E40AF]/20',
          'group overflow-hidden',
          'border-2 border-[#1E40AF]/10'
        )}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={isOpen ? t('common.chatbot.close') : t('common.chatbot.open')}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full h-full flex items-center justify-center bg-[#1E40AF] rounded-full"
            >
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative w-full h-full"
            >
              {/* Yima Avatar */}
              <div className="w-full h-full rounded-full p-1 relative">
                <img
                  src={yimaAvatar}
                  alt="Yima"
                  width={48}
                  height={48}
                  loading="eager"
                  decoding="async"
                  className="w-full h-full object-cover rounded-full"
                />
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center">
                  <span className="w-4 h-4 bg-slate-400 rounded-full border-2 border-white shadow-lg" />
                  <Moon className="w-2.5 h-2.5 text-white absolute" aria-hidden="true" />
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Window - Premium Institutional Design */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop for mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 md:hidden"
            />

            {/* Chat Container */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className={cn(
                'fixed z-50',
                'md:bottom-24 md:right-6',
                'bottom-4 right-4 left-4 sm:left-auto sm:right-4',
                'md:w-[400px] sm:w-[360px] w-auto',
                'md:h-[580px] h-[70vh] max-h-[580px]',
                'rounded-3xl',
                'bg-white',
                'border border-slate-200',
                'shadow-[0_25px_80px_rgba(0,0,0,0.15)]',
                'flex flex-col',
                'overflow-hidden'
              )}
            >
              {/* Header - Cream/Minimal Design */}
              <div className="relative bg-gradient-to-br from-slate-50 to-white px-5 py-4 border-b border-slate-100 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-white shadow-md overflow-hidden ring-2 ring-[#1E40AF]/10">
                        <img
                          src={yimaAvatar}
                          alt="Yima"
                          width={48}
                          height={48}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-slate-400 rounded-full flex items-center justify-center border-2 border-white">
                        <Moon className="w-2 h-2 text-white" aria-hidden="true" />
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-serif font-bold text-slate-900 tracking-tight">Yima</h3>
                        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 rounded-full">
                          {t('common.chatbot.status.resting')}
                        </span>
                      </div>
                      <p className="text-xs text-slate-700 mt-0.5 font-medium">
                        {t('common.chatbot.subtitle')}
                      </p>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-700"
                    aria-label={t('common.chatbot.close')}
                  >
                    <X className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>

              {/* Chat Body - Elegant Coming Soon State */}
              <div
                ref={chatBodyRef}
                className="flex-1 overflow-y-auto px-6 py-8 bg-gradient-to-b from-white to-slate-50/50"
              >
                <div className="flex flex-col items-center justify-center h-full text-center space-y-6">
                  {/* Elegant Avatar with subtle animation */}
                  <motion.div
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                    className="relative"
                  >
                    {/* Decorative rings */}
                    <div className="absolute inset-0 -m-4">
                      <motion.div 
                        animate={{ rotate: 360 }}
                        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                        className="w-full h-full rounded-full border border-[#1E40AF]/10"
                      />
                    </div>
                    <div className="absolute inset-0 -m-8">
                      <motion.div 
                        animate={{ rotate: -360 }}
                        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
                        className="w-full h-full rounded-full border border-dashed border-amber-300/40"
                      />
                    </div>
                    
                    <div className="w-28 h-28 rounded-full bg-white shadow-xl p-1 relative">
                      <div className="w-full h-full rounded-full overflow-hidden ring-2 ring-[#1E40AF]/5">
                        <img
                          src={yimaAvatar}
                          alt="Yima"
                          width={112}
                          height={112}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover opacity-90"
                        />
                      </div>
                    </div>
                    
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                      className="absolute -bottom-2 -right-2 w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center shadow-lg border-3 border-white"
                    >
                      <span className="text-xl">💤</span>
                    </motion.div>
                  </motion.div>

                  {/* Message - Premium Typography */}
                  <div className="space-y-3 max-w-[280px]">
                    <h4 className="text-2xl font-serif font-bold text-slate-900 leading-tight">
                      {t('common.chatbot.sick.title')}
                    </h4>
                    <p className="text-sm text-slate-700 leading-relaxed">
                      {t('common.chatbot.sick.message')}
                    </p>
                  </div>

                  {/* Elegant Return Notice */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="w-full max-w-[260px]"
                  >
                    <div className="relative px-5 py-4 bg-gradient-to-br from-[#1E40AF]/5 to-transparent rounded-2xl border border-[#1E40AF]/10">
                      <div className="flex items-center gap-2 justify-center">
                        <Moon className="w-4 h-4 text-[#1E40AF]" aria-hidden="true" />
                        <p className="text-sm text-[#1E40AF] font-semibold">
                          {t('common.chatbot.recovery.estimate')}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Subtle decorative element */}
                  <div className="flex items-center gap-2 opacity-40">
                    <div className="w-8 h-px bg-slate-300" />
                    <Heart className="w-3 h-3 text-slate-400" aria-hidden="true" />
                    <div className="w-8 h-px bg-slate-300" />
                  </div>
                </div>
              </div>

              {/* Input Area - Elegant Disabled State */}
              <div className="px-4 py-4 bg-white border-t border-slate-100 flex-shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled
                    placeholder={t('common.chatbot.input.placeholder')}
                    className={cn(
                      'w-full px-4 py-3.5 pr-12',
                      'bg-slate-50 text-slate-700',
                      'border border-slate-200 rounded-2xl',
                      'focus:outline-none',
                      'cursor-not-allowed',
                      'text-sm placeholder:text-slate-500'
                    )}
                    title={t('common.chatbot.input.disabledTooltip')}
                  />
                  <button
                    disabled
                    className={cn(
                      'absolute right-2 top-1/2 -translate-y-1/2',
                      'w-9 h-9 rounded-xl',
                      'bg-slate-200 text-slate-700',
                      'flex items-center justify-center',
                      'cursor-not-allowed'
                    )}
                    title={t('common.chatbot.input.disabledTooltip')}
                    aria-label={t('common.chatbot.input.send')}
                  >
                    <Send className="w-4 h-4" aria-hidden="true" />
                  </button>
                </div>
                
                {/* Development notice - subtle */}
                <p className="text-[11px] text-slate-700 text-center mt-3 font-medium opacity-70">
                  {t('common.chatbot.developmentNotice')}
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
